import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Input, Spin, Button, message, Select } from "antd";
import {
    SearchOutlined,
    EyeOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import { getRequest } from "../../../../service/index";
import ExcelJS from "exceljs";
import { getEmployeeData } from "../../../../utils/localStorage";
import { formatDate } from "../../../../utils/dateTime";
import ModalWrapper from "../../../../Modal";
import EditEmployees from '../../../../modals/bulkEditEmployee';

const MyEmployee = () => {

    const userData = getEmployeeData();
    const role = userData?.role;
    const employeePermissions = userData?.designation?.permissions?.Employee || {};
    const allEmployeeAccess = employeePermissions['All Employee Access'] || [];
    const canView = role === "companyadmin" || (Array.isArray(allEmployeeAccess) && allEmployeeAccess.includes("view"));
    const canExport = role === "companyadmin" || (Array.isArray(allEmployeeAccess) && allEmployeeAccess.includes("export"));
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [data, setData] = useState({
        employees: [],
        pagination: {
            totalCount: 0,
            pageNumber: 1,
            pageSize: 10,
            totalPages: 0,
        },
    });
    const navigate = useNavigate();

    // Status filter options
    const statusOptions = [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inActive", label: "Inactive" }
    ];

    // Debounced search function
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (searchTerm) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    fetchEmployees(1, data.pagination.pageSize, searchTerm);
                }, 500); // 500ms delay
            };
        })(),
        [data.pagination.pageSize]
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
    };

    // Handle status filter change
    const handleStatusChange = (value) => {
        setStatusFilter(value);
        fetchEmployees(1, data.pagination.pageSize, search, value);
    };

    const fetchEmployees = async (page = 1, pageSize = 10, searchTerm = search, statusTerm = statusFilter) => {
        try {
            setLoading(true);
            if (!canView) {
                setData({
                    employees: [],
                    pagination: {
                        totalCount: 0,
                        pageNumber: 1,
                        pageSize: pageSize,
                        totalPages: 0,
                    },
                });
                return;
            }

            // Build query parameters
            const queryParams = new URLSearchParams({
                pageNumber: page.toString(),
                pageSize: pageSize.toString()
            });

            // Add search parameter if provided
            if (searchTerm && searchTerm.trim()) {
                queryParams.append('search', searchTerm.trim());
            }

            // Add status parameter if not "all"
            if (statusTerm && statusTerm !== "all") {
                queryParams.append('status', statusTerm);
            }

            let response;
            if (role === "companyadmin") {
                response = await getRequest(
                    `/api/employees?${queryParams.toString()}`
                );
            } else {
                queryParams.append('managerId', userData?.userId);
                response = await getRequest(
                    `/api/employees?${queryParams.toString()}`
                );
            }

            const apiPagination = response?.pagination || response?.meta || {};
            const normalizedPageSize = apiPagination.pageSize ?? apiPagination.limit ?? response?.limit ?? pageSize;
            const totalCount =
                apiPagination.totalCount ??
                apiPagination.totalRecords ??
                response?.totalRecords ??
                response?.totalCount ??
                (Array.isArray(response?.employees) ? response.employees.length : 0);
            const pageNumber = apiPagination.pageNumber ?? apiPagination.currentPage ?? response?.currentPage ?? page;
            const totalPages = apiPagination.totalPages ?? (normalizedPageSize ? Math.ceil(totalCount / normalizedPageSize) : 0);

            setData({
                employees: Array.isArray(response?.employees) ? response.employees : [],
                pagination: {
                    totalCount,
                    pageNumber,
                    pageSize: normalizedPageSize,
                    totalPages,
                },
            });
        } catch (error) {
            console.error("Error fetching employees:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView]);

    // Columns
    const columns = [
        { title: "ID", dataIndex: "employee_code", key: "employee_code" },
        {
            title: "User",
            key: "name",
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full overflow-hidden flex items-center justify-center">
                        {record.image ? (
                            <img
                                src={record.image}
                                alt={record.firstName}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-sm font-semibold text-gray-600">
                                {`${record.firstName.charAt(0)}${record.lastName.charAt(0)}`}
                            </span>
                        )}
                    </div>
                    {`${record.firstName} ${record.lastName}`}
                </div>
            ),
        },
        { title: "Department", dataIndex: "department", key: "department" },
        { title: "Designation", dataIndex: "designation", key: "designation" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <span
                    className={`px-2 py-1 rounded-full text-md font-semibold ${status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                        }`}
                >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
            ),
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <div className="flex gap-3 justify-center">
                    <EyeOutlined
                        className="text-gray-500 cursor-pointer hover:text-gray-800"
                        onClick={() => navigate(`/view-employee/${record.id || record._id}`)}
                    />
                </div>
            ),
        },
    ];

    const handleTableChange = (pagination) => {
        fetchEmployees(pagination.current, pagination.pageSize, search, statusFilter);
    };

    const handleExportAll = async () => {
        try {
            message.loading("Fetching all employee data for export...", 0);

            // Build query parameters for export
            const queryParams = new URLSearchParams();
            if (search && search.trim()) {
                queryParams.append('search', search.trim());
            }
            if (statusFilter && statusFilter !== "all") {
                queryParams.append('status', statusFilter);
            }

            let response;
            if (role === "companyadmin") {
                response = await getRequest(`/api/employees${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
            } else {
                queryParams.append('managerId', userData?.userId);
                response = await getRequest(`/api/employees?${queryParams.toString()}`);
            }

            if (response?.employees?.length > 0) {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Employees");

                // Add Header Row
                const headers = [
                    "Employee Code",
                    "Full Name",
                    "Father Name",
                    "Status",
                    "Email Address",
                    "Contact Number",
                    "National ID",
                    "Machine Code",
                    "Designation",
                    "Department",
                    "Hire Date",
                    "Monthly Salary",
                    "Address",
                    "Shift Name",
                    "Shift Start Time",
                    "Shift End Time",
                ];

                worksheet.addRow(headers);

                // Style header
                worksheet.getRow(1).eachCell((cell) => {
                    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "366092" },
                    };
                    cell.alignment = { horizontal: "center", vertical: "middle" };
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };
                });

                // Add employee rows with conditional styling
                response.employees.forEach((emp) => {
                    const row = worksheet.addRow([
                        emp.employee_code || "N/A",
                        `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
                        emp.fatherName || "N/A",
                        emp.status || "N/A",
                        emp.emailAddress || "N/A",
                        emp.contact_number || "N/A",
                        emp.nationalId || "N/A",
                        emp.machineCode || "N/A",
                        emp.designation || "N/A",
                        emp.department || "N/A",
                        emp.hire_date
                            ? formatDate(emp.hire_date)
                            : "N/A",
                        emp.monthly_salary || 0,
                        emp.address || "N/A",
                        emp.shift?.name || "N/A",
                        emp.shift?.startTime || "N/A",
                        emp.shift?.endTime || "N/A",
                    ]);

                    // Add borders to each cell in the row
                    row.eachCell((cell) => {
                        cell.border = {
                            top: { style: "thin" },
                            bottom: { style: "thin" },
                            left: { style: "thin" },
                            right: { style: "thin" },
                        };
                    });

                    // Conditional formatting
                    if (emp.status?.toLowerCase() === "inactive") {
                        row.eachCell((cell) => {
                            cell.fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FFCCCC" }, // Light Red
                            };
                        });
                    } else {
                        row.eachCell((cell) => {
                            cell.fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "CCFFCC" }, // Light Green
                            };
                        });
                    }
                });

                // Auto column width
                worksheet.columns.forEach((col) => {
                    let maxLength = 0;
                    col.eachCell({ includeEmpty: true }, (cell) => {
                        const val = cell.value ? cell.value.toString() : "";
                        maxLength = Math.max(maxLength, val.length);
                    });
                    col.width = maxLength < 15 ? 15 : maxLength + 2;
                });

                // Save file
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const currentDate = new Date().toISOString().split("T")[0];
                a.download = `Employees_List_${currentDate}.xlsx`;
                a.click();

                message.destroy();
                message.success("Employee data exported successfully!");
            } else {
                message.destroy();
                message.warning("No employee data available for export");
            }
        } catch (error) {
            message.destroy();
            console.error("Export error:", error);
            message.error("Failed to export employee data. Please try again.");
        }
    };

    return (
        <>
            <div className="flex flex-col p-4">
                <div className="w-full flex justify-between items-center mb-6 mt-4">
                    <h4 className="text-2xl font-semibold">Employee List</h4>
                </div>

                <div className="w-full flex flex-col gap-3">
                    {/* Search + Export */}
                    <div className="flex justify-between items-center gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="relative flex-1">
                                <Input
                                    placeholder="Search by employee name or employee id"
                                    value={search}
                                    onChange={handleSearchChange}
                                    prefix={<SearchOutlined className="text-gray-500" />}
                                    className="px-4 py-3 rounded-lg h-10"
                                />
                            </div>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                options={statusOptions}
                                className="min-w-[120px]"
                                placeholder="Status"
                            />
                        </div>
                        <Button
                            onClick={() => setIsEditOpen(true)}
                            size="large"
                        >
                            Edit Bulk Employees
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!canExport}
                            onClick={() => canExport ? handleExportAll() : message.warning("You don't have permission to export employees.")}
                            className="bg-primary text-white"
                            size="large"
                        >
                            Export All
                        </Button>
                    </div>

                    {/* Table */}
                    <Spin spinning={loading}>
                        <Table
                            className="bg-white rounded-lg border border-gray-200"
                            columns={columns}
                            scroll={{ x: "max-content" }}
                            pagination={{
                                total: data.pagination.totalCount,
                                pageSize: data.pagination.pageSize,
                                current: data.pagination.pageNumber,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} items`,
                            }}
                            dataSource={canView ? data.employees : []}
                            rowKey={(record) => record.id || record._id}
                            onChange={handleTableChange}
                        />
                    </Spin>
                    {!canView && (
                        <div className="text-center text-gray-500 py-4">You don't have permission to view Employee.</div>
                    )}
                </div>
            </div>
            <ModalWrapper
                open={isEditOpen}
                onRequestClose={() => setIsEditOpen(false)}
                title="Edit Employees"
            >
                <EditEmployees onClose={() => setIsEditOpen(false)} />
            </ModalWrapper>
        </>
    );
};

export default MyEmployee;
